import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { driveService } from "../../modules/vault/api";
import { importableMimeToBridgeRoute } from "../../modules/vault/lib/openLinkedItem";

/**
 * "Open with → WeCo" for the desktop app (apps/weco-desktop).
 *
 * When someone opens a .docx/.doc/.xlsx/.xls/.csv/.pptx/.ppt from their
 * computer with WeCo, the desktop shell queues the file and this bridge, which
 * only mounts inside the signed-in <Shell />, takes it, uploads it to the
 * user's Vault ("From my computer" folder) and opens it through the same
 * import bridge Vault's own "Open" uses (`importableMimeToBridgeRoute` →
 * /write|grid|present/import-from-vault/:itemId). So it gets the same
 * server-side conversion (.doc/.xls/.ppt via LibreOffice), malware scan and
 * Vault ↔ document link as a file uploaded in the browser.
 *
 * Files opened before sign-in stay queued in the desktop app and open right
 * after login. In a normal browser `window.wecoDesktop` is absent and this
 * renders nothing.
 */

export interface DesktopOpenedFile {
  name: string;
  /** IPC structured-clones the bytes into a plain (non-shared) ArrayBuffer, which `File` requires. */
  data: Uint8Array<ArrayBuffer>;
}

export interface DesktopTakeFilesResult {
  files: DesktopOpenedFile[];
  rejected: { name: string; reason: "too-large" | "unreadable" }[];
}

interface WecoDesktopFileBridge {
  takeOpenedFiles?: () => Promise<DesktopTakeFilesResult>;
  onFilesOpened?: (callback: () => void) => () => void;
}

declare global {
  interface Window {
    wecoDesktop?: WecoDesktopFileBridge;
  }
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Finds (or creates) the root Vault folder desktop-opened files go into; null → Vault root. */
async function resolveUploadFolder(folderName: string): Promise<string | null> {
  try {
    const { results } = await driveService.getItems({ type: "folder", page_size: 200 });
    const existing = results.find((item) => item.type === "folder" && item.name === folderName);
    if (existing) return existing.id;
    return (await driveService.createFolder({ name: folderName })).id;
  } catch {
    return null;
  }
}

/** Uploads every file, opens the first in its editor and offers the rest via toasts. */
export async function openDesktopFiles(
  batch: DesktopTakeFilesResult,
  navigate: (path: string) => void,
  t: Translate,
): Promise<void> {
  for (const { name, reason } of batch.rejected) {
    toast.error(t(reason === "too-large" ? "desktop.openFile.tooLarge" : "desktop.openFile.unreadable", { name }));
  }

  const openable = batch.files.filter(({ name }) => {
    if (importableMimeToBridgeRoute(null, "_", name)) return true;
    toast.error(t("desktop.openFile.unsupported", { name }));
    return false;
  });
  if (openable.length === 0) return;

  const folderId = await resolveUploadFolder(t("desktop.openFile.folderName"));
  let openedOne = false;

  for (const { name, data } of openable) {
    const toastId = toast.loading(t("desktop.openFile.opening", { name }));
    try {
      const item = await driveService.uploadFile(new File([data], name), folderId);
      const route = importableMimeToBridgeRoute(item.mime_type, item.id, item.name || name);
      if (!route) throw new Error(`No editor for ${name}`);
      if (!openedOne) {
        openedOne = true;
        toast.dismiss(toastId);
        navigate(route);
      } else {
        toast.success(t("desktop.openFile.savedToVault", { name }), {
          id: toastId,
          action: { label: t("desktop.openFile.open"), onClick: () => navigate(route) },
        });
      }
    } catch (err) {
      console.error("Failed to open desktop file", name, err);
      toast.error(t("desktop.openFile.failed", { name }), { id: toastId });
    }
  }
}

export function DesktopOpenFilesBridge() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const desktop = window.wecoDesktop;
    const takeOpenedFiles = desktop?.takeOpenedFiles;
    if (!takeOpenedFiles || !desktop.onFilesOpened) return;

    let running = false;
    let again = false;
    const drain = async () => {
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        do {
          again = false;
          const batch = await takeOpenedFiles();
          if (batch.files.length > 0 || batch.rejected.length > 0) await openDesktopFiles(batch, navigate, t);
        } while (again);
      } catch (err) {
        console.error("Failed to take files from the desktop app", err);
      } finally {
        running = false;
      }
    };

    const unsubscribe = desktop.onFilesOpened(() => void drain());
    void drain();
    return unsubscribe;
  }, [navigate, t]);

  return null;
}
